import { t } from 'ttag';
import classNames from 'classnames';

import { getPathWithoutLang } from '@utils/i18n';
import { Link } from '@ui/Link';

import s from './ProductDetailsCharacteristics.sss';

interface CharacteristicChoise {
  id: number,
  title: string,
  isIndexed: boolean,
}

export interface Characteristic {
  id: number,
  title: string,
  choices: CharacteristicChoise[],
}

interface Color {
  id: number,
  name: string,
  hex: string,
  isIndexed: boolean
}

interface Size {
  id: number,
  name: string,
  primarySizeName: string,
  secondarySizeName?: string | null,
}

interface Brand {
  id: number
  name: string
  isIndexed: boolean
}

export interface Product {
  id: number,
  condition?: {
    id: number,
    name: string,
  } | null,
  brand?: Brand | null
  colors?: (Color | null)[] | null,
  sizes: Size[],
}

export interface Catalog {
  id: string,
  url: string,
  breadcrumbs: {
    id: string,
    name: string,
    url: string,
  }[],
  sizeGroup?: {
    id: number,
    name: string,
  } | null,
}

interface Props {
  className?: string,
  product: Product,
  catalog?: Catalog | null,
  characteristics: Characteristic[],
}

export const ProductDetailsCharacteristics: React.FC<Props> = ({
  className,
  product,
  catalog,
  characteristics,
}) => {
  return (
    <ul className={classNames(s.list, className)}>
      {!!product.condition && (
        <li className={s.item}>
          <p className={s.label}>
            {t`Стан:`}
          </p>

          <p className={s.value}>
            {product.condition?.name}
          </p>
        </li>
      )}

      {!!product.colors?.length && (
        <li className={s.item}>
          <p className={s.label}>
            {t`Колір:`}
          </p>

          <ul className={s.valueList}>
            {product.colors.map((color) => {
              if (color) {
                return (
                  <li
                      key={color.id}
                      className={s.valueItem}
                  >
                    <span
                      className={s.colorsHex}
                      style={{
                        background: color.hex === '0'
                          ? 'rgba(0, 0, 0, 0) linear-gradient(rgb(255, 237, 36), rgb(66, 255, 110), rgb(121, 149, 255)) repeat scroll 0% 0%'
                          : `#${color.hex}`,
                      }}
                    />
                    {
                      color.isIndexed
                        ? (
                          <Link
                            theme='black'
                            to={`${catalog?.url}/if/colors=${color.id}`}
                          >
                            {color.name}
                          </Link>
                        )
                        : (
                          <span className={s.colorsTitle}>
                            {color.name}
                          </span>
                        )
                    }
                  </li>
                );
              }
              return null;
            })}
          </ul>
        </li>
      )}

      {!!product.sizes.length && !!catalog?.sizeGroup && (
        <li className={s.item}>
          <p className={s.label}>
            {t`Розмір:`}
          </p>

          <ul className={s.valueList}>
            {product.sizes.map((size) => (
              <li
                key={size.id}
                className={s.valueItem}
              >
                {size.name.replace(/\s/g, '')}
              </li>
            ))}
          </ul>
        </li>
      )}

      <li className={s.item}>
        <p className={s.label}>
          {t`Категорії:`}
        </p>

        <ul className={s.valueList}>
          {catalog?.breadcrumbs.slice(0).reverse().slice(0, 2).map((category) => (
            <li
              key={category.id}
              className={s.valueItem}
            >
              <Link
                theme='black'
                to={getPathWithoutLang(category.url)}
              >
                {category.name}
              </Link>
            </li>
          ))}
        </ul>
      </li>

      {characteristics.map((characteristic) => (
        <li
          key={characteristic.id}
          className={s.item}
        >
          <p className={s.label}>
            {characteristic.title}
          </p>

          <ul className={s.valueList}>
            {characteristic.choices.map((choice) => {
              if (choice.isIndexed) {
                return (
                  <li
                    key={choice.id}
                    className={s.valueItem}
                  >
                    <Link
                      key={choice.id}
                      theme='black'
                      to={`${catalog?.url}/if/characteristics=${choice.id}`}
                    >
                      {choice.title}
                    </Link>
                  </li>
                );
              }

              return (
                <li
                  key={choice.id}
                  className={s.valueItem}
                >
                  {choice.title}
                </li>
              );
            })}
          </ul>
        </li>
      ))}
      {
        product.brand?.isIndexed && (
          <li className={s.item}>
            <p className={s.label}>
              {t`Бренд:`}
            </p>

            <ul className={s.valueList}>
              <li className={s.valueItem}>
                <Link
                  theme='black'
                  to={`${catalog?.url}/if/brands=${product.brand.id}`}
                >
                  {product.brand.name}
                </Link>
              </li>
            </ul>
          </li>
        )
      }
    </ul>
  );
};
